// Tab "Bezug" (B1-B29 and the note of APP-PARITY.md), after the UX line of 19.10.2026: the main page answers "what is
// next, how were the last ones" (presets as one row of segments, the recipe at a glance, the last five shots); the
// recipe editor, one shot, the presets editor, the grinder, beans and the history are pages of their own. Decisions in
// core/screens/brew.ts.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  amountView,
  bagLeftG,
  beanLabel,
  brewMoreView,
  chipsView,
  controlView,
  dayText,
  dotJoin,
  doseExtra,
  formatNumber,
  LAST_PRESET_KEY,
  piHint,
  ratioStepTarget,
  recipePresets,
  fineTuning,
  listErrorText,
  historyGroups,
  MANUAL_BACK_KEY,
  manualStep,
  nextShotView,
  presetNumberStep,
  presetRows,
  presetSteps,
  presetsEditor,
  PRESET_COMMIT_MS,
  reasonText,
  recipeSummary,
  savePreset,
  shotBarsView,
  shotsCard,
  SHOTS_MAIN,
  stopCommand,
  takeCurrent,
  targetExtra,
  type BrewPage,
  type ManualBack,
  type Preset,
} from '../../core/index.js';
import {
  Banner,
  Button,
  Card,
  CardHeader,
  FactRow,
  Icon,
  LaneChart,
  LinkSelect,
  NavList,
  Pending,
  PickTile,
  Segmented,
  Sheet,
  Stepper,
} from '../components/index.js';
import { useMachine, useStore } from '../machine-context.js';
import {
  ActionButton,
  ControlRow,
  OwnerNote,
  Row,
  ShotDays,
  SubScreen,
  TabScreen,
  TextCommit,
} from './parts.js';
import { BeanPage, BeansPage, NewBeanPage } from './BeansPages.js';
import { ComparePage, HistoryPage } from './HistoryPages.js';
import { ShotPage } from './ShotParts.js';
// Claude support (src/ui/ai): the grinder lookup on the grinder's page, Claude's open suggestion at the next shot
import { GrinderInfo } from '../ai/GrinderInfo.js';
import { NextClaude } from '../ai/NextClaude.js';

export function BrewTab({
  title,
  accountButton,
  page = { page: 'main' },
  onPage = () => undefined,
  onReplace = onPage,
  onBack = () => undefined,
}: {
  title: string;
  accountButton?: ReactNode;
  /** the sub-page shown (#bezug/rezept …), the main page by default */
  page?: BrewPage;
  onPage?: (p: BrewPage) => void;
  /** a page that ends in another one: the new bean becomes its page, back leads to the beans */
  onReplace?: (p: BrewPage) => void;
  onBack?: () => void;
}) {
  switch (page.page) {
    case 'recipe':
      return <RecipePage onBack={onBack} onPage={onPage} />;
    case 'fine':
      return <FinePage onBack={onBack} />;
    case 'shot':
      return <ShotPage id={page.id} onBack={onBack} onOpen={(id) => onPage({ page: 'shot', id })} />;
    case 'presets':
      return <PresetsPage onBack={onBack} onPage={onPage} />;
    case 'preset':
      return <PresetPage index={page.index} onBack={onBack} />;
    case 'grinder':
      return <GrinderPage onBack={onBack} />;
    case 'beans':
      return <BeansPage onPage={onPage} onBack={onBack} />;
    case 'newBean':
      return <NewBeanPage onBack={onBack} onCreated={(b) => onReplace({ page: 'bean', id: b.id })} />;
    case 'bean':
      return <BeanPage id={page.id} onBack={onBack} onOpenShot={(id) => onPage({ page: 'shot', id })} />;
    case 'history':
      return <HistoryPage onPage={onPage} onBack={onBack} />;
    case 'compare':
      return <ComparePage ids={page.ids} onBack={onBack} />;
    default:
      return (
        <TabScreen tab="bezug" title={title} accountButton={accountButton}>
          <NextShotCard onPage={onPage} />
          <ShotsCard onPage={onPage} />
          <MoreCard onPage={onPage} />
        </TabScreen>
      );
  }
}

// ------------------------------------------------------------------------------------------------ next shot

function readManualBack(): ManualBack | null {
  try {
    return JSON.parse(localStorage.getItem(MANUAL_BACK_KEY) ?? 'null') as ManualBack | null;
  } catch {
    return null;
  }
}

function writeManualBack(v: ManualBack | null): void {
  try {
    if (v) localStorage.setItem(MANUAL_BACK_KEY, JSON.stringify(v));
    else localStorage.removeItem(MANUAL_BACK_KEY);
  } catch {
    // per-browser convenience only (orione.manualBack of the local app)
  }
}

function readLastPreset(machineId: string): number | null {
  try {
    const v = Number(localStorage.getItem(`${LAST_PRESET_KEY}.${machineId}`));
    return Number.isInteger(v) && v >= 0 ? v : null;
  } catch {
    return null;
  }
}

function writeLastPreset(machineId: string, index: number): void {
  try {
    localStorage.setItem(`${LAST_PRESET_KEY}.${machineId}`, String(index));
  } catch {
    // per-browser convenience only: which preset a changed recipe came from
  }
}

/** Choosing a preset (the bean through the library first) or "Manuell", for the main page and the recipe page. */
function usePresets() {
  const { ctx, client, machineId, send } = useMachine();
  const beans = client.beans(machineId);
  const [error, setError] = useState<string | null>(null);
  const apply = async (index: number) => {
    const back = readManualBack();
    writeManualBack(null);
    writeLastPreset(machineId, index);
    setError(null);
    const plan = presetSteps(ctx, index, back, beans.getSnapshot());
    // the bean first, through the library: the machine gets exactly its recipe (concept §2.5), then the rest
    if (plan.select) {
      const r = await beans.select(plan.select);
      if (!r.ok) {
        setError(reasonText(ctx.lang, r.error.code));
        return;
      }
    }
    for (const params of plan.sets) {
      const o = await send({ kind: 'set', params });
      if (o.result !== 'done') return;
    }
  };
  const manual = () => {
    const m = manualStep(ctx);
    if (!m) return;
    writeManualBack(m.remember);
    void send({ kind: 'set', params: m.params });
  };
  return { apply, manual, error, last: readLastPreset(machineId) };
}

/**
 * The next shot at a glance (prototype "Reiter Bezug"): the presets as one row of segments (never wrapping), the bean
 * as a tile, the recipe in figures, "Anpassen" to the recipe page, the scale with "Tarieren". A running shot is in the
 * live bar above the tab bar and on the machine tab.
 */
function NextShotCard({ onPage }: { onPage: (p: BrewPage) => void }) {
  const { ctx } = useMachine();
  const v = nextShotView(ctx);
  const chips = chipsView(ctx);
  const presets = usePresets();
  const chipError = presets.error;
  const applyPreset = presets.apply;
  const goManual = presets.manual;
  return (
    <Card className="tab-card" data-testid="next-shot">
      <CardHeader
        title={v.title}
        action={
          <button
            type="button"
            className="link-btn"
            onClick={() => onPage({ page: 'recipe' })}
            data-testid="recipe-edit"
          >
            {ctx.tr('recipeAdjust')}
            <Icon name="chevronRight" size="sm" />
          </button>
        }
      />
      <div data-testid="chips">
        <Segmented<number | 'manual'>
          label={ctx.tr('sPresets')}
          options={[
            ...chips.items.map((c) => ({ value: c.index, label: c.name, on: c.on, hint: c.sub })),
            {
              value: 'manual' as const,
              label: chips.manual.label,
              on: chips.manual.on,
              hint: chips.manual.sub,
            },
          ]}
          disabled={!chips.enabled}
          onPick={(x) => (x === 'manual' ? goManual() : void applyPreset(x))}
          itemTestId="chip"
        />
      </div>
      {chipError && (
        <p className="field-error" role="alert" data-testid="chip-error">
          {chipError}
        </p>
      )}
      <RecipeGlance onOpen={() => onPage({ page: 'beans' })} openTestId="bean-open" />
      <NextClaude onOpenShot={(id) => onPage({ page: 'shot', id })} />
      {v.scale && (
        <div className="scale-row">
          <p className={v.scale.connected ? 'scale-line is-on' : 'scale-line'} data-testid="scale-line">
            {v.scale.text}
            {v.scale.battery && (
              <span className={v.scale.battery.low ? 'is-low' : undefined}> · {v.scale.battery.text}</span>
            )}
          </p>
          {v.tare && <ActionButton action={v.tare} variant="ghost" testId="tare" />}
        </div>
      )}
    </Card>
  );
}

/**
 * The recipe at a glance (also on the machine tab): the coffee as a tile (the bean and its day; on the machine tab the
 * chosen preset over it), then dose, stop, ratio over grind, temperature, pre-infusion, each with its name above. The
 * tile opens what changes it.
 */
export function RecipeGlance({
  onOpen,
  showPreset = false,
  openTestId,
}: {
  onOpen?: () => void;
  /** the chosen preset as the tile's title, the bean below it (machine tab) */
  showPreset?: boolean;
  openTestId?: string;
}) {
  const { ctx } = useMachine();
  const r = recipeSummary(ctx);
  const chips = chipsView(ctx);
  const preset = showPreset
    ? (chips.items.find((c) => c.on)?.name ?? (chips.manual.on ? chips.manual.label : null))
    : null;
  return (
    <div className="recipe-glance" data-testid="recipe-glance">
      <PickTile
        eyebrow={ctx.tr(preset ? 'pickChosen' : 'pickBean')}
        title={preset ?? <span className="recipe-bean">{r.bean}</span>}
        sub={
          preset ? (
            <>
              <span className="recipe-bean">{r.bean}</span>
              {r.day && ` · ${r.day}`}
            </>
          ) : (
            r.day
          )
        }
        action={ctx.tr('recipeEdit')}
        onClick={onOpen}
        testId={openTestId}
      />
      <FactRow rows items={r.facts.map((f) => f && { ...f, testId: `recipe-${f.key}` })} />
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ recipe page

/**
 * Everything of the next shot (prototype "Rezept", B5-B19): the presets on top (the chosen one stays while its values
 * change, "Zurücksetzen", "Als … speichern" at the end), the bean as a row, the amount from dose and ratio with the
 * weight it stops at, grind and temperature as values to tap, pre-infusion, how it stops, the fine tuning one page
 * deeper. Every change is one absolute set (AD-11).
 */
function RecipePage({ onBack, onPage }: { onBack: () => void; onPage: (p: BrewPage) => void }) {
  const { ctx, client, machineId, send, stepper } = useMachine();
  const presets = usePresets();
  const pv = recipePresets(ctx, presets.last);
  const v = nextShotView(ctx);
  const r = recipeSummary(ctx);
  const bv = useStore(client.beans(machineId));
  const sv = useStore(client.shots(machineId));
  const am = amountView(ctx);
  const [editing, setEditing] = useState<'temp' | null>(null);
  const bean = bv.items.find((b) => b.current);
  const left = bagLeftG(bean, sv);
  const beanHint = dotJoin([
    r.day ?? '',
    bean?.bag?.roastDay !== undefined && bean.bag.roastDay !== null
      ? ctx.tr('roastedOn', { d: dayText(bean.bag.roastDay, ctx.lang) })
      : '',
    left !== null ? ctx.tr('bagLeft', { g: left }) : '',
  ]);
  const target = 'brew.by_weight.target_weight';
  const pendingTarget = stepper.pending(target);
  const ratio =
    pendingTarget !== null && ctx.num('brew.dose') > 0
      ? pendingTarget / ctx.num('brew.dose')
      : (am.ratio ?? null);
  const grind = controlView(ctx, 'brew.grind', { label: 'grindLabel' });
  // "93 °C" as on the board, "93,5 °C" when it has a half
  const setpoint = controlView(ctx, 'brew.setpoint', { label: 'brewTemp', whole: true });
  const pi = controlView(ctx, 'brew.pre_infusion.enabled', { label: 'sPreinf' });
  return (
    <SubScreen tab="bezug" title={ctx.tr('recipeTitle')} onBack={onBack} testId="recipe-page">
      <div className="stack-tight">
        <Segmented<number | 'manual'>
          label={ctx.tr('sPresets')}
          options={pv.options}
          disabled={!pv.enabled}
          onPick={(x) => (x === 'manual' ? presets.manual() : void presets.apply(x))}
          itemTestId="recipe-preset"
        />
        {pv.changed && (
          <div className="recipe-changed" data-testid="recipe-changed">
            <span>{pv.changed}</span>
            <button
              type="button"
              className="link-btn"
              onClick={() => typeof pv.chosen === 'number' && void presets.apply(pv.chosen)}
              data-testid="recipe-reset"
            >
              {ctx.tr('resetRecipe')}
            </button>
          </div>
        )}
        {presets.error && (
          <p className="field-error" role="alert">
            {presets.error}
          </p>
        )}
      </div>
      <Card className="tab-card nav-card" data-testid="recipe-bean">
        <NavList
          items={[
            {
              key: 'bean',
              // the roaster with the name (Dominik 23.10.2026), not part of the name itself
              label: bean ? beanLabel(bean) : r.bean,
              hint: beanHint || undefined,
              onClick: () => onPage({ page: 'beans' }),
              testId: 'recipe-bean-open',
            },
          ]}
        />
      </Card>
      <Card className="tab-card" data-testid="recipe-card">
        <CardHeader
          title={ctx.tr('amount')}
          info={am.mode === 'weight' ? { text: ctx.tr('amountInfo'), label: ctx.tr('infoShow') } : null}
        />
        <ControlRow
          control={controlView(ctx, 'brew.dose', { label: 'dose' })}
          options={{ extra: doseExtra(ctx, (id) => client.watch(machineId).value(id)) }}
          testId="dose"
        />
        {am.mode === 'weight' && ratio !== null && (
          <Row label={ctx.tr('ratio')} testId="ratio">
            <Stepper
              value={`1:${formatNumber(ratio, 1, ctx.lang)}`}
              provisional={pendingTarget !== null}
              disabled={!am.editable}
              onMinus={() => stepper.set(target, ratioStepTarget(ctx, ratio, -1))}
              onPlus={() => stepper.set(target, ratioStepTarget(ctx, ratio, 1))}
              minusLabel={ctx.tr('ratioLess')}
              plusLabel={ctx.tr('ratioMore')}
              minusTestId="ratio-minus"
              plusTestId="ratio-plus"
              valueTestId="ratio-value"
            />
          </Row>
        )}
        {am.mode === 'time' && (
          <ControlRow
            control={controlView(ctx, 'brew.by_time.target_time', {
              label: 'targetTime',
              ...(v.targetTime?.hint ? { info: 'afterPiInfo' } : {}),
            })}
            options={{ extra: targetExtra(ctx, 'brew.by_time.target_time') }}
            testId="target-time"
          />
        )}
        {am.stop && (
          <div className="stop-box" data-testid="stops-at">
            <Icon name="stopAt" />
            <span className="stop-box-label">{am.stop.label}</span>
            <span className="stop-box-value num">
              {pendingTarget !== null ? `${formatNumber(pendingTarget, 1, ctx.lang)}\u00a0g` : am.stop.value}
            </span>
          </div>
        )}
      </Card>
      <Card className="tab-card rows-card" data-testid="recipe-values">
        {grind && (
          <div className="row" data-testid="grind">
            <div className="row-text">
              <span className="row-label">{grind.label}</span>
            </div>
            <GrindChip value={typeof grind.value === 'string' ? grind.value : ''} editable={grind.editable} />
          </div>
        )}
        {setpoint && (
          <div className="row" data-testid="brew-setpoint">
            <div className="row-text">
              <span className="row-label">{setpoint.label}</span>
            </div>
            <button
              type="button"
              className="value-chip num"
              disabled={!setpoint.editable}
              onClick={() => setEditing('temp')}
              data-testid="brew-setpoint-value"
            >
              {setpoint.text}
            </button>
          </div>
        )}
        {/* how long the burst and the pause are under its label (board "Rezept"; they are set under Feineinstellung) */}
        <ControlRow control={pi && { ...pi, hint: piHint(ctx) }} testId="pi-switch" />
      </Card>
      <Card className="tab-card" data-testid="stop-card">
        {/* when it stops without a scale and at the latest: behind the (i), not printed under the choice */}
        <CardHeader
          title={v.stop.label}
          info={{ text: v.stop.hint, label: ctx.tr('infoShow'), testId: 'stop-info' }}
        />
        <div className="stack-tight" data-testid="stop-row">
          <Segmented
            label={v.stop.label}
            options={v.stop.modes}
            disabled={!v.stop.editable}
            onPick={(mode) => void send(stopCommand(ctx, mode))}
            testId="stop-mode"
          />
        </div>
      </Card>
      <Card className="tab-card nav-card" data-testid="fine-row">
        <NavList
          items={[
            {
              key: 'fine',
              label: ctx.tr('fine'),
              hint: ctx.tr('fineHint'),
              onClick: () => onPage({ page: 'fine' }),
              testId: 'fine-open',
            },
          ]}
        />
      </Card>
      {pv.saveLabel && typeof pv.chosen === 'number' && (
        <Button
          block
          onClick={() => typeof pv.chosen === 'number' && void send(takeCurrent(ctx, pv.chosen))}
          data-testid="recipe-save"
        >
          {pv.saveLabel}
        </Button>
      )}
      {editing === 'temp' && setpoint && (
        <Sheet
          variant="sheet"
          title={setpoint.label}
          titleId="recipe-temp-title"
          onClose={() => setEditing(null)}
          testId="temp-sheet"
        >
          <ControlRow control={setpoint} bare testId="brew-setpoint-step" />
          <Button block onClick={() => setEditing(null)} data-testid="temp-sheet-done">
            {ctx.tr('done')}
          </Button>
        </Sheet>
      )}
    </SubScreen>
  );
}

/** The grind size as a value to tap (prototype "Rezept"): it is a field in the look of a chip, sent when left. */
function GrindChip({ value, editable }: { value: string; editable: boolean }) {
  const { send } = useMachine();
  return (
    <ChipInput
      value={value}
      editable={editable}
      onCommit={(v) => void send({ kind: 'set', params: { 'brew.grind': v } })}
      testId="grind-text"
    />
  );
}

/** A short text as a value chip at the end of its row (the grind size), sent when the field is left or on Enter. */
function ChipInput({
  value,
  editable,
  onCommit,
  testId,
}: {
  value: string;
  editable: boolean;
  onCommit: (v: string) => void;
  testId: string;
}) {
  const { ctx } = useMachine();
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft !== null && draft.trim() !== value) onCommit(draft.trim());
    setDraft(null);
  };
  const text = draft ?? value;
  // the chip's width from the text's length, not from `size` (WebKit sizes that with Poppins' average character: 105
  // instead of 72 px for "2,5" on the iPhone)
  const width = text.length > 6 ? ' is-longer' : text.length > 4 ? ' is-long' : '';
  return (
    <input
      className={`value-chip num${width}`}
      aria-label={ctx.tr('grindLabel')}
      value={text}
      placeholder="–"
      maxLength={9}
      disabled={!editable}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      data-testid={testId}
    />
  );
}

/**
 * The seldom-changed values of the recipe (board "Feineinstellung"): the temperature profile with what it means at the
 * current target, how a shot by weight stops (lag with grams at the last flow, the time without a scale), the
 * pre-infusion; each a card of rows, the explanations behind the (i).
 */
function FinePage({ onBack }: { onBack: () => void }) {
  const { ctx, client, machineId } = useMachine();
  const shots = useStore(client.shots(machineId));
  const fine = fineTuning(ctx, shots);
  const lag = controlView(ctx, 'brew.by_weight.lag', { label: 'lag', info: 'lagInfo', unit: 's' });
  return (
    <SubScreen tab="bezug" title={fine.title} onBack={onBack} testId="fine-page">
      <Card className="tab-card rows-card" data-testid="fine-body">
        <CardHeader
          title={ctx.tr('tempProfile')}
          info={{ text: ctx.tr('tempProfileInfo'), label: ctx.tr('infoShow'), testId: 'temp-end-info' }}
        />
        <ControlRow
          control={controlView(ctx, 'brew.temp_end', { label: 'tempEndRow', zeroText: 'zeroOff' })}
          testId="temp-end"
        />
        {fine.profileLine && (
          <p className="hint card-foot" data-testid="temp-end-line">
            {fine.profileLine}
          </p>
        )}
      </Card>
      {(fine.lag || fine.fallbackTime) && (
        <Card className="tab-card rows-card" data-testid="stop-fine">
          <CardHeader title={ctx.tr('stop')} />
          {fine.lag && (
            // what the lag means in grams at the last flow, under its label (board: "~2,0 g bei 2,3 g/s")
            <ControlRow control={lag && { ...lag, hint: fine.lag.hint }} testId="lag" />
          )}
          {fine.fallbackTime && (
            <ControlRow
              control={controlView(ctx, 'brew.by_time.target_time', {
                label: 'fallbackTime',
                info: 'fallbackInfo',
                whole: true,
              })}
              testId="fallback-time"
            />
          )}
        </Card>
      )}
      <Card className="tab-card rows-card" data-testid="pi-card">
        <CardHeader title={ctx.tr('sPreinf')} />
        <ControlRow
          control={controlView(ctx, 'brew.pre_infusion.time', { label: 'piBurst', unit: 's' })}
          testId="pi-burst"
        />
        <ControlRow
          control={controlView(ctx, 'brew.pre_infusion.pause', { label: 'piPause', unit: 's' })}
          testId="pi-pause"
        />
      </Card>
    </SubScreen>
  );
}

// ------------------------------------------------------------------------------------------------ presets page

/**
 * The presets (prototype "Schnellwahl"): a list, the one used last marked, each with what it does; a row opens its
 * fields; "Aktuelles Rezept speichern" asks which of the three takes the recipe as it is now.
 */
function PresetsPage({ onBack, onPage }: { onBack: () => void; onPage: (p: BrewPage) => void }) {
  const { ctx, machineId, send } = useMachine();
  const rows = presetRows(ctx, readLastPreset(machineId));
  const [saving, setSaving] = useState(false);
  return (
    <SubScreen
      tab="bezug"
      title={ctx.tr('sPresets')}
      info={{ text: ctx.tr('presetsIntro'), label: ctx.tr('infoShow'), testId: 'presets-info' }}
      onBack={onBack}
      testId="presets-page"
    >
      <Card className="tab-card nav-card" data-testid="preset-list">
        <NavList
          items={rows.map((r) => ({
            key: String(r.index),
            label: (
              <>
                {r.tag && <span className="label pick-eyebrow preset-tag">{r.tag}</span>}
                {r.name}
              </>
            ),
            hint: r.summary,
            onClick: () => onPage({ page: 'preset', index: r.index }),
            testId: `preset-open-${r.index}`,
          }))}
        />
      </Card>
      <Button
        variant="secondary"
        icon="plus"
        disabled={!ctx.canEdit('brew.presets')}
        onClick={() => setSaving(true)}
        data-testid="preset-save-current"
      >
        {ctx.tr('saveCurrent')}
      </Button>
      {saving && (
        <Sheet
          variant="sheet"
          title={ctx.tr('saveCurrentTitle')}
          titleId="preset-save-title"
          onClose={() => setSaving(false)}
          testId="preset-save-sheet"
        >
          <div className="sheet-actions">
            {rows.map((r) => (
              <Button
                key={r.index}
                variant="secondary"
                block
                onClick={() => {
                  setSaving(false);
                  void send(takeCurrent(ctx, r.index));
                }}
                data-testid={`preset-save-${r.index}`}
              >
                {ctx.tr('saveInto', { p: r.name })}
              </Button>
            ))}
          </div>
        </Sheet>
      )}
    </SubScreen>
  );
}

/** One preset's fields (B20-B24): name, bean, dose, grind, temperature, time, ratio, pre-infusion. */
function PresetPage({ index, onBack }: { index: number; onBack: () => void }) {
  const { ctx, client, machineId, send } = useMachine();
  const bv = useStore(client.beans(machineId));
  const ed = presetsEditor(ctx, bv);
  const item = ed.items[index] ?? ed.items[0];
  const save = (i: number, patch: Partial<Preset>) => void send(savePreset(ctx, i, patch));
  return (
    <SubScreen
      tab="bezug"
      title={item?.preset.name ?? ctx.tr('sPresets')}
      onBack={onBack}
      testId="preset-page"
    >
      {item && <PresetForm key={item.index} item={item} ratio={ed.ratio} enabled={ed.enabled} save={save} />}
    </SubScreen>
  );
}

/**
 * One preset as its board ("Eine Schnellwahl: Doppio"): the name in a card; the bean (as the row's value with its
 * list), dose, grind and temperature as rows; how it stops (the brew time, by weight the ratio); the pre-infusion as
 * segments; "Aktuelle Werte übernehmen". Dose and temperature may be "–" (the preset leaves them alone): + starts at
 * the recipe's value now. The stop mode and deleting a preset are not in the firmware's presets (brew.presets keeps
 * three, without a stop mode); the boards "Schnellwahl" and "Eine Schnellwahl" show it the same way since 23.10.2026.
 */
function PresetForm({
  item: { index, preset: p, beanChoices },
  ratio,
  enabled,
  save,
}: {
  item: ReturnType<typeof presetsEditor>['items'][number];
  ratio: boolean;
  enabled: boolean;
  save: (i: number, patch: Partial<Preset>) => void;
}) {
  const { ctx, send } = useMachine();
  const dose = p.dose || ctx.num('brew.dose', 18);
  return (
    <>
      <Card className="tab-card" data-testid={`preset-${index}`}>
        <TextCommit
          id={`preset-${index}-name`}
          label={ctx.tr('presetName')}
          value={p.name}
          maxLength={10}
          disabled={!enabled}
          onCommit={(v) => save(index, { name: v })}
          testId={`preset-${index}-name`}
        />
      </Card>
      <Card className="tab-card rows-card" data-testid={`preset-${index}-values`}>
        <Row label={ctx.tr('beans')} info={ctx.tr('presetBeanInfo')} testId={`preset-${index}-bean-row`}>
          <LinkSelect
            id={`preset-${index}-bean`}
            label={ctx.tr('beans')}
            text={p.bean || ctx.tr('presetKeep')}
            value={p.bean}
            disabled={!enabled}
            onChange={(e) => save(index, { bean: e.target.value })}
            data-testid={`preset-${index}-bean`}
          >
            <option value="">{ctx.tr('presetKeep')}</option>
            {beanChoices.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </LinkSelect>
        </Row>
        <PresetNumber
          label={ctx.tr('dose')}
          value={p.dose}
          step={0.5}
          min={5}
          max={30}
          unset={ctx.num('brew.dose', 18)}
          unit="g"
          zeroText="–"
          disabled={!enabled}
          onCommit={(v) => save(index, { dose: v })}
          testId={`preset-${index}-dose`}
        />
        <Row label={ctx.tr('grindLabel')} testId={`preset-${index}-grind`}>
          <ChipInput
            value={p.grind}
            editable={enabled}
            onCommit={(v) => save(index, { grind: v })}
            testId={`preset-${index}-grind-text`}
          />
        </Row>
        <PresetNumber
          label={ctx.tr('presetTemp')}
          value={p.temp}
          step={0.5}
          min={80}
          max={105}
          unset={ctx.num('brew.setpoint', 93)}
          unit="°C"
          zeroText="–"
          disabled={!enabled}
          onCommit={(v) => save(index, { temp: v })}
          testId={`preset-${index}-temp`}
        />
      </Card>
      <Card className="tab-card rows-card" data-testid={`preset-${index}-stop`}>
        <CardHeader
          title={ctx.tr('presetStop')}
          info={{
            text: ctx.tr('presetStopInfo'),
            label: ctx.tr('infoShow'),
            testId: `preset-${index}-stop-info`,
          }}
        />
        <PresetNumber
          label={ctx.tr('presetTime')}
          value={p.s}
          step={0.5}
          min={5}
          max={120}
          unit="s"
          disabled={!enabled}
          onCommit={(v) => save(index, { s: v })}
          testId={`preset-${index}-time`}
        />
        {ratio && (
          <PresetNumber
            label={ctx.tr('presetRatio')}
            hint={ctx.tr('presetStopsAt', { g: formatNumber(Math.round(dose * p.r * 2) / 2, 1, ctx.lang) })}
            value={p.r}
            step={0.1}
            min={1}
            max={10}
            prefix="1:"
            disabled={!enabled}
            onCommit={(v) => save(index, { r: v })}
            testId={`preset-${index}-ratio`}
          />
        )}
      </Card>
      <Card className="tab-card" data-testid={`preset-${index}-pi-card`}>
        <CardHeader title={ctx.tr('sPreinf')} />
        <Segmented
          label={ctx.tr('sPreinf')}
          options={[
            { value: -1, label: ctx.tr('presetKeep'), on: p.pi < 0 },
            { value: 1, label: ctx.tr('on'), on: p.pi === 1 },
            { value: 0, label: ctx.tr('off'), on: p.pi === 0 },
          ]}
          disabled={!enabled}
          onPick={(pi) => save(index, { pi })}
          testId={`preset-${index}-pi`}
        />
      </Card>
      <Button
        variant="secondary"
        block
        disabled={!enabled}
        onClick={() => void send(takeCurrent(ctx, index))}
        data-testid={`preset-${index}-take`}
      >
        {ctx.tr('presetTake')}
      </Button>
    </>
  );
}

/** −/+ for a number inside brew.presets (one string for all three recipes), sent 700 ms after the last tap. */
function PresetNumber({
  label,
  hint,
  value,
  step,
  min,
  max,
  unset,
  unit,
  prefix,
  zeroText,
  disabled,
  onCommit,
  testId,
}: {
  label: string;
  hint?: ReactNode;
  value: number;
  step: number;
  min: number;
  max: number;
  /** the value may be "–" (0): the first + from it starts here (presetNumberStep) */
  unset?: number;
  unit?: string;
  prefix?: string;
  zeroText?: string;
  disabled?: boolean;
  onCommit: (v: number) => void;
  testId?: string;
}) {
  const { ctx } = useMachine();
  const [pending, setPending] = useState<number | null>(null);
  const commit = useRef(onCommit);
  useEffect(() => {
    commit.current = onCommit;
  }, [onCommit]);
  useEffect(() => {
    if (pending === null) return;
    const id = setTimeout(() => {
      commit.current(pending);
      setPending(null);
    }, PRESET_COMMIT_MS);
    return () => clearTimeout(id);
  }, [pending]);
  const v = pending ?? value;
  // the ratio always with its tenth ("1:2,0"), other numbers as on the board: "18 g", "18,5 g", "30 s"
  const digits = prefix ? 1 : v % 1 ? 1 : 0;
  const text =
    v === 0 && zeroText
      ? zeroText
      : `${prefix ?? ''}${v.toLocaleString(ctx.lang === 'de' ? 'de-DE' : 'en-GB', {
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
        })}${unit ? `\u00a0${unit}` : ''}`;
  return (
    <Row label={label} hint={hint} className="row-stepper" testId={testId}>
      <Stepper
        value={text}
        provisional={pending !== null}
        disabled={disabled}
        onMinus={() => setPending(presetNumberStep(v, -1, step, min, max, unset))}
        onPlus={() => setPending(presetNumberStep(v, 1, step, min, max, unset))}
        minusLabel={`${label} −`}
        plusLabel={`${label} +`}
        minusTestId={testId ? `${testId}-minus` : undefined}
        plusTestId={testId ? `${testId}-plus` : undefined}
        valueTestId={testId ? `${testId}-value` : undefined}
      />
    </Row>
  );
}

// ------------------------------------------------------------------------------------------------ grinder page

/**
 * The grinder (B17, B18; board "Mühle"): its name, which way its numbers go as two segments (UX line: two to four
 * choices are segments; none chosen while it is not set, never "unbekannt"), and what Claude knows about it.
 */
function GrinderPage({ onBack }: { onBack: () => void }) {
  const { ctx, send } = useMachine();
  const name = controlView(ctx, 'brew.grinder', { label: 'grinderName' });
  const scale = controlView(ctx, 'brew.grind_scale');
  return (
    <SubScreen tab="bezug" title={ctx.tr('grinder')} onBack={onBack} testId="grinder-page">
      <OwnerNote controls={[name, scale]} />
      <Card className="tab-card" data-testid="grinder-card">
        <ControlRow control={name} testId="grinder" />
        {scale && (
          <div className="field" data-testid="grind-scale-row">
            <span className="field-label" aria-hidden="true">
              {ctx.tr('grinderHigher')}
            </span>
            <Segmented
              label={ctx.tr('grinderHigher')}
              options={[
                { value: 1, label: ctx.tr('grinderFiner'), on: scale.value === 1 },
                { value: 2, label: ctx.tr('grinderCoarser'), on: scale.value === 2 },
              ]}
              disabled={!scale.editable}
              onPick={(v) => void send({ kind: 'set', params: { 'brew.grind_scale': v } })}
              testId="grind-scale"
            />
          </div>
        )}
      </Card>
      {/* what Claude knows about the grinder (src/ui/ai), a card of its own */}
      <GrinderInfo />
    </SubScreen>
  );
}

// ------------------------------------------------------------------------------------------------ last shots

/**
 * The last shots by day (prototype "Reiter Bezug": a row opens the shot's page), the columns of the last twelve
 * below, "Verlauf ›" to all.
 */
function ShotsCard({ onPage }: { onPage: (p: BrewPage) => void }) {
  const { ctx, client, machineId } = useMachine();
  const sv = useStore(client.shots(machineId));
  const beans = useStore(client.beans(machineId));
  const card = shotsCard(ctx, sv, beans);
  const groups = historyGroups(ctx, sv.items.slice(0, SHOTS_MAIN), beans);
  const bars = shotBarsView(ctx, sv.items);
  return (
    <Card className="tab-card" data-testid="shots-card">
      <CardHeader
        title={card.title}
        action={
          <button
            type="button"
            className="link-btn"
            onClick={() => onPage({ page: 'history' })}
            data-testid="shots-history"
          >
            {ctx.tr('shotHistory')}
            <Icon name="chevronRight" size="sm" />
          </button>
        }
      />
      {card.loading && <Pending label={ctx.tr('loading')} />}
      {card.empty && <p className="hint">{card.empty}</p>}
      {card.error && <Banner tone="danger">{listErrorText(ctx.lang, card.error)}</Banner>}
      <div className="shot-days" data-testid="shot-list">
        <ShotDays
          groups={groups}
          onOpen={(id) => onPage({ page: 'shot', id })}
          rowTestId={(id) => `shot-${id}`}
        />
      </div>
      {bars && (
        <div className="shot-bars" data-testid="shot-bars">
          <LaneChart
            view={bars.chart}
            info={{ text: ctx.tr('barsInfo'), label: ctx.tr('infoShow') }}
            testId="bars"
          />
        </div>
      )}
    </Card>
  );
}

/** The seldom-used pages of the tab, each with what it holds now (UX line: one level deeper). */
function MoreCard({ onPage }: { onPage: (p: BrewPage) => void }) {
  const { ctx, client, machineId } = useMachine();
  const bv = useStore(client.beans(machineId));
  const sv = useStore(client.shots(machineId));
  const m = brewMoreView(ctx, bv, sv);
  return (
    <Card className="tab-card nav-card" data-testid="brew-more">
      <NavList
        items={[
          {
            key: 'beans',
            label: ctx.tr('beansTitle'),
            hint: m.beans,
            onClick: () => onPage({ page: 'beans' }),
            testId: 'beans-all',
          },
          {
            key: 'presets',
            label: ctx.tr('sPresets'),
            hint: m.presets,
            onClick: () => onPage({ page: 'presets' }),
            testId: 'presets-open',
          },
          {
            key: 'grinder',
            label: ctx.tr('grinder'),
            hint: m.grinder,
            onClick: () => onPage({ page: 'grinder' }),
            testId: 'grinder-open',
          },
        ]}
      />
    </Card>
  );
}
